import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { updateProDriverMutation } from "@/app/ProDrivers/Drivers/GraphQL/proDriverMutations";
import { RecordScreenFormCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenFormCard";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { TextAreaInput } from "@/lib/Components/Form/Inputs/TextAreaInput";
import { DateOfBirthInput } from "@/lib/Components/Form/Inputs/DateOfBirthInput";
import { CityInput } from "@/app/Cities/Cities/Components/CityInput";
import { LicenseTypeInput } from "@/app/ProDrivers/Drivers/Components/LicenseTypeInput";
import { TaxRegistrationInput } from "@/app/ProDrivers/Drivers/Components/TaxRegistrationInput";
import { TowVehicleTypeInput } from "@/app/ProDrivers/Drivers/Components/TowVehicleTypeInput";
import {
  ProDriverLicenseType,
  ProDriverTowVehicleType,
  TaxRegistration,
  UpdateProDriverInput,
} from "@/gql/graphql";
import { useFormState } from "@/lib/Components/Form/Hooks/useFormState";
import { SwitchInput } from "@/lib/Components/Form/Inputs/SwitchInput";
import { NumberInput } from "@/lib/Components/Form/Inputs/NumberInput";
import { useTranslations } from "use-intl";

/**
 * Minimal driver shape the inline detail cards need. Both the assignment
 * record query and the pro-driver drawer query satisfy this.
 */
export type ProDriverDetailCardsDriver = {
  id: string;
  name: string | null;
  email?: string | null;
  phone?: string | null;
  date_of_birth?: string | null;
  basedIn?: { id: string; name: string } | null;
  license_type: ProDriverLicenseType;
  license_number?: string | null;
  license_issuer?: string | null;
  tax_registration: TaxRegistration;
  business_registration_number?: string | null;
  bank_name?: string | null;
  bank_account_name?: string | null;
  bank_account_number?: string | null;
  bank_identifier_code?: string | null;
  has_tow_vehicle: boolean;
  tow_vehicle_type?: ProDriverTowVehicleType | null;
  tow_vehicle_year?: number | null;
  tow_capacity_kg?: number | null;
  bio?: string | null;
};

const GRID = "grid-cols-2 lg:grid-cols-2";

/** email is displayed from the user record but never submitted. */
type ContactFormValues = UpdateProDriverInput & { email: string };

/**
 * Inline, per-section editable driver detail cards (Contact, License, Tax,
 * Bank, Bio). Shared by the assignment sheet and the pro-driver drawer.
 *
 * Each field renders its own read/edit state (via GenericField), so the read
 * and edit views share one grid — the layout does not shift on toggle.
 */
export function ProDriverDetailCards({
  proDriver,
}: {
  proDriver: ProDriverDetailCardsDriver;
}) {
  const { mutateAsync } = useGqlMutation(updateProDriverMutation);
  const tTow = useTranslations("proDriver.towVehicle");

  return (
    <div className="space-y-6">
      <RecordScreenFormCard<ContactFormValues>
        title="Contact Information"
        className={GRID}
        initialValues={{
          name: proDriver.name ?? "",
          email: proDriver.email ?? "",
          phone: proDriver.phone ?? "",
          date_of_birth: proDriver.date_of_birth ?? "",
          basedIn: proDriver.basedIn?.id
            ? { connect: proDriver.basedIn.id }
            : undefined,
        }}
        onSubmit={async (values) => {
          await mutateAsync({
            id: proDriver.id,
            input: {
              name: values.name,
              phone: values.phone || null,
              date_of_birth: values.date_of_birth || null,
              basedIn: values.basedIn,
            },
          });
        }}
      >
        <ContactFormFields />
      </RecordScreenFormCard>

      <RecordScreenFormCard<UpdateProDriverInput>
        title="License Information"
        className={GRID}
        initialValues={{
          license_type: proDriver.license_type,
          license_number: proDriver.license_number ?? "",
          license_issuer: proDriver.license_issuer ?? "",
        }}
        onSubmit={async (values) => {
          await mutateAsync({
            id: proDriver.id,
            input: {
              license_type: values.license_type,
              license_number: values.license_number || null,
              license_issuer: values.license_issuer || null,
            },
          });
        }}
      >
        <LicenseFormFields />
      </RecordScreenFormCard>

      <RecordScreenFormCard<UpdateProDriverInput>
        title={tTow("title")}
        className={GRID}
        initialValues={{
          has_tow_vehicle: proDriver.has_tow_vehicle,
          tow_vehicle_type: proDriver.tow_vehicle_type ?? undefined,
          tow_vehicle_year: proDriver.tow_vehicle_year ?? undefined,
          tow_capacity_kg: proDriver.tow_capacity_kg ?? undefined,
        }}
        onSubmit={async (values) => {
          await mutateAsync({
            id: proDriver.id,
            input: {
              has_tow_vehicle: values.has_tow_vehicle,
              tow_vehicle_type: values.has_tow_vehicle
                ? values.tow_vehicle_type
                : null,
              tow_vehicle_year: values.has_tow_vehicle
                ? values.tow_vehicle_year || null
                : null,
              tow_capacity_kg: values.has_tow_vehicle
                ? values.tow_capacity_kg || null
                : null,
            },
          });
        }}
      >
        <TowVehicleFormFields />
      </RecordScreenFormCard>

      <RecordScreenFormCard<UpdateProDriverInput>
        title="Tax & Business"
        className={GRID}
        initialValues={{
          tax_registration: proDriver.tax_registration,
          business_registration_number:
            proDriver.business_registration_number ?? "",
        }}
        onSubmit={async (values) => {
          await mutateAsync({
            id: proDriver.id,
            input: {
              tax_registration: values.tax_registration,
              business_registration_number:
                values.business_registration_number || null,
            },
          });
        }}
      >
        <TaxFormFields />
      </RecordScreenFormCard>

      <RecordScreenFormCard<UpdateProDriverInput>
        title="Bank Details"
        className={GRID}
        initialValues={{
          bank_name: proDriver.bank_name ?? "",
          bank_account_name: proDriver.bank_account_name ?? "",
          bank_account_number: proDriver.bank_account_number ?? "",
          bank_identifier_code: proDriver.bank_identifier_code ?? "",
        }}
        onSubmit={async (values) => {
          await mutateAsync({
            id: proDriver.id,
            input: {
              bank_name: values.bank_name || null,
              bank_account_name: values.bank_account_name || null,
              bank_account_number: values.bank_account_number || null,
              bank_identifier_code: values.bank_identifier_code || null,
            },
          });
        }}
      >
        <BankFormFields />
      </RecordScreenFormCard>

      <RecordScreenFormCard<UpdateProDriverInput>
        title="Bio"
        initialValues={{
          bio: proDriver.bio ?? "",
        }}
        onSubmit={async (values) => {
          await mutateAsync({
            id: proDriver.id,
            input: {
              bio: values.bio,
            },
          });
        }}
      >
        <TextAreaInput name="bio" label="Bio" className="col-span-full" />
      </RecordScreenFormCard>
    </div>
  );
}

function ContactFormFields() {
  return (
    <>
      <TextInput name="name" label="Name" className="col-span-full" />
      <TextInput
        name="email"
        label="Email"
        type="email"
        isEditable={false}
        tooltip="Owned by the driver's user account - changing it requires the verified email change flow."
        className="col-span-1"
      />
      <TextInput name="phone" label="Phone" className="col-span-1" />
      <DateOfBirthInput
        name="date_of_birth"
        label="Date of Birth"
        className="col-span-1"
      />
      <CityInput
        name="basedIn.connect"
        label="Based In"
        className="col-span-1"
      />
    </>
  );
}

function LicenseFormFields() {
  return (
    <>
      <LicenseTypeInput
        label="License Type"
        name="license_type"
        className="col-span-1"
      />
      <TextInput
        name="license_number"
        label="License Number"
        className="col-span-1"
      />
      <TextInput
        name="license_issuer"
        label="License Issuer"
        className="col-span-1"
      />
    </>
  );
}

function BankFormFields() {
  return (
    <>
      <TextInput name="bank_name" label="Bank Name" className="col-span-1" />
      <TextInput
        name="bank_account_name"
        label="Account Name"
        className="col-span-1"
      />
      <TextInput
        name="bank_account_number"
        label="Account Number"
        className="col-span-1"
      />
      <TextInput
        name="bank_identifier_code"
        label="Bank Identifier Code"
        className="col-span-1"
      />
    </>
  );
}

function TowVehicleFormFields() {
  const { values } = useFormState<UpdateProDriverInput>();
  const t = useTranslations("proDriver.towVehicle");

  return (
    <>
      <SwitchInput
        name="has_tow_vehicle"
        label={t("hasTowVehicle")}
        className="col-span-1"
      />
      {values.has_tow_vehicle ? (
        <>
          <TowVehicleTypeInput
            name="tow_vehicle_type"
            label={t("type")}
            className="col-span-1"
          />
          <NumberInput
            name="tow_vehicle_year"
            label={t("year")}
            className="col-span-1"
          />
          <NumberInput
            name="tow_capacity_kg"
            label={t("capacity")}
            suffix={t("capacityUnit")}
            className="col-span-1"
          />
        </>
      ) : null}
    </>
  );
}

function TaxFormFields() {
  const { values } = useFormState<UpdateProDriverInput>();

  return (
    <>
      <TaxRegistrationInput
        name="tax_registration"
        label="Tax Registration"
        className="col-span-1"
      />
      {values.tax_registration !== TaxRegistration.None ? (
        <TextInput
          name="business_registration_number"
          label="Business Registration Number"
          className="col-span-1"
        />
      ) : null}
    </>
  );
}
